// Pure geometry for the laskuri: point-in-shape, thickness fields, bboxes,
// shape transforms and move-snapping. World units: meters. y grows DOWNWARD
// (screen convention) - rect corner order is [nw, ne, se, sw].
import { LIMITS } from "./planModel.js";

export function pointInRect(px, py, s) {
  return px >= s.x && px <= s.x + s.w && py >= s.y && py <= s.y + s.h;
}

export function pointInTri(px, py, [[ax, ay], [bx, by], [cx, cy]]) {
  const d1 = (px - bx) * (ay - by) - (ax - bx) * (py - by);
  const d2 = (px - cx) * (by - cy) - (bx - cx) * (py - cy);
  const d3 = (px - ax) * (cy - ay) - (cx - ax) * (py - ay);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos); // handles both windings; boundary counts as inside
}

export function pointInCircle(px, py, s) {
  const dx = px - s.cx, dy = py - s.cy;
  return dx * dx + dy * dy <= s.r * s.r;
}

export function pointInShape(px, py, s) {
  if (s.type === "rect") return pointInRect(px, py, s);
  if (s.type === "tri") return pointInTri(px, py, s.points);
  if (s.type === "circle" || s.type === "pillar") return pointInCircle(px, py, s);
  return false;
}

export function triArea(points) {
  const [[ax, ay], [bx, by], [cx, cy]] = points;
  return Math.abs((bx - ax) * (cy - ay) - (cx - ax) * (by - ay)) / 2;
}

/** Analytic area - for labels and sanity checks; the engine uses sampling. */
export function shapeArea(s) {
  if (s.type === "rect") return s.w * s.h;
  if (s.type === "tri") return triArea(s.points);
  if (s.type === "circle" || s.type === "pillar") return Math.PI * s.r * s.r;
  return 0;
}

export function shapeBBox(s) {
  if (s.type === "rect") return { minX: s.x, minY: s.y, maxX: s.x + s.w, maxY: s.y + s.h };
  if (s.type === "circle" || s.type === "pillar") return { minX: s.cx - s.r, minY: s.cy - s.r, maxX: s.cx + s.r, maxY: s.cy + s.r };
  const xs = s.points.map((p) => p[0]), ys = s.points.map((p) => p[1]);
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

export function planBBox(shapes) {
  if (!shapes.length) return null;
  const bs = shapes.map(shapeBBox);
  return {
    minX: Math.min(...bs.map((b) => b.minX)),
    minY: Math.min(...bs.map((b) => b.minY)),
    maxX: Math.max(...bs.map((b) => b.maxX)),
    maxY: Math.max(...bs.map((b) => b.maxY)),
  };
}

/** Slab thickness of shape s at world point (px,py). Point assumed inside s. */
export function shapeThicknessAt(px, py, s) {
  const t = s.thickness;
  if (t.mode === "uniform") return t.value;
  if (s.type === "rect") {
    const u = (px - s.x) / s.w, v = (py - s.y) / s.h;
    const [nw, ne, se, sw] = t.values;
    return (nw * (1 - u) + ne * u) * (1 - v) + (sw * (1 - u) + se * u) * v;
  }
  // triangle: barycentric, values[i] <-> points[i]
  const [[ax, ay], [bx, by], [cx, cy]] = s.points;
  const det = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
  const l1 = ((by - cy) * (px - cx) + (cx - bx) * (py - cy)) / det;
  const l2 = ((cy - ay) * (px - cx) + (ax - cx) * (py - cy)) / det;
  const [va, vb, vc] = t.values;
  return va * l1 + vb * l2 + vc * (1 - l1 - l2);
}

export function translateShape(s, dx, dy) {
  if (s.type === "rect") return { ...s, x: s.x + dx, y: s.y + dy };
  if (s.type === "circle" || s.type === "pillar") return { ...s, cx: s.cx + dx, cy: s.cy + dy };
  return { ...s, points: s.points.map(([x, y]) => [x + dx, y + dy]) };
}

const clampSize = (v) => Math.min(LIMITS.maxSize, Math.max(LIMITS.minSize, v));

/** Resize rect by dragging corner 0=nw 1=ne 2=se 3=sw to world (wx,wy). */
export function resizeRectCorner(s, corner, wx, wy) {
  let { x, y, w, h } = s;
  const right = x + w, bottom = y + h;
  if (corner === 0) { w = clampSize(right - wx); h = clampSize(bottom - wy); x = right - w; y = bottom - h; }
  else if (corner === 1) { w = clampSize(wx - x); h = clampSize(bottom - wy); y = bottom - h; }
  else if (corner === 2) { w = clampSize(wx - x); h = clampSize(wy - y); }
  else if (corner === 3) { w = clampSize(right - wx); h = clampSize(wy - y); x = right - w; }
  return { ...s, x, y, w, h };
}

/** Move triangle vertex vi to (wx,wy); refuses near-degenerate results. */
export function moveTriVertex(s, vi, wx, wy) {
  const points = s.points.map((p, i) => (i === vi ? [wx, wy] : p));
  return triArea(points) < 0.005 ? s : { ...s, points };
}

export function resizeCircle(s, wx, wy) {
  const r = Math.hypot(wx - s.cx, wy - s.cy);
  const [lo, hi] = s.type === "pillar"
    ? [LIMITS.minPillarDiameter / 2, LIMITS.maxPillarDiameter / 2]
    : [LIMITS.minSize / 2, LIMITS.maxSize / 2];
  return { ...s, r: Math.min(hi, Math.max(lo, r)) };
}

export const snapToGrid = (v, grid = 0.05) => Math.round(v / grid) * grid;

/**
 * Snap a move gesture. Magnetizes the moved shape's bbox edges to other
 * shapes' bbox edges within `tolerance` (world meters - the canvas converts
 * ~12 screen px via 12 / viewport.scale); otherwise falls back to the 5 cm grid.
 */
export function snapMove(s, dx, dy, others, { tolerance, grid = 0.05 } = {}) {
  const bb = shapeBBox(s);
  const otherBoxes = others.map(shapeBBox);
  const guides = [];
  const solve = (axis, lo, hi, delta, base, edges) => {
    let best = null;
    for (const cand of edges) {
      for (const edge of [lo, hi]) {
        const adj = cand - edge;
        if (Math.abs(adj) <= tolerance && (!best || Math.abs(adj) < Math.abs(best.adj))) best = { adj, value: cand };
      }
    }
    if (best) { guides.push({ axis, value: best.value }); return delta + best.adj; }
    return snapToGrid(base + delta, grid) - base;
  };
  const outDx = solve("x", bb.minX + dx, bb.maxX + dx, dx, bb.minX, otherBoxes.flatMap((b) => [b.minX, b.maxX]));
  const outDy = solve("y", bb.minY + dy, bb.maxY + dy, dy, bb.minY, otherBoxes.flatMap((b) => [b.minY, b.maxY]));
  return { dx: outDx, dy: outDy, guides };
}
